import {
  installDateOverrideFrom,
  jobCardInstallSpan,
  londonDayKey,
  shiftDayKey,
  toInstallTimeOnly,
  type InstallCalendarEvent,
} from './jobCardInstallDate';
import { latestInstallEventsByProject, type InstallEventRow } from './fitterJobList';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';

export type OpenJobCardSectionId = 'overdue' | 'installed' | 'this_week' | 'next_week' | 'later' | 'no_date';

export const OPEN_JOB_CARD_SECTIONS: Array<{ id: OpenJobCardSectionId; title: string; hint: string }> = [
  { id: 'overdue', title: 'Overdue', hint: 'Install date has passed and the card is not marked complete.' },
  { id: 'installed', title: 'Installed', hint: 'Install date has passed and the card is marked complete.' },
  { id: 'this_week', title: 'This week', hint: 'On site at some point from today to Sunday.' },
  { id: 'next_week', title: 'Next week', hint: 'Starts next Monday to Sunday.' },
  { id: 'later', title: 'Later', hint: 'Starts after next week.' },
  { id: 'no_date', title: 'Date pending', hint: 'Raised, but no install date yet: the backlog. Use Installation date pending on a card to move a booked job here.' },
];

export type JobCardRow = {
  id: string;
  project_id: string;
  title?: string | null;
  created_at: string;
  install_date_override?: string | null;
  install_end_date_override?: string | null;
  follows_booking?: boolean | null;
};

export type JobCompletionRow = {
  job_card_id?: string | null;
  action?: string | null;
  /** Ended cards: the card the job carried on under. */
  continued_on_card_id?: string | null;
  job_complete?: boolean | null;
  attested_at: string;
  actor_name?: string | null;
};

export type JobCardCompletion = {
  at: string;
  by: string;
  /** ended = closed without finishing; the job carries on under a later card. */
  kind: 'completed' | 'ended';
};

export type OpenJobCardProject = {
  id: string;
  project_name?: string | null;
  customer_name?: string | null;
  lifecycle_ref?: string | null;
  install_date?: string | null;
  metadata?: unknown;
  site_address?: string | null;
  project_status?: 'active' | 'completed' | 'cancelled' | null;
  status?: string | null;
  complete?: boolean | null;
  workflow_tasks?: Array<{ task: string; completed?: boolean }> | null;
};

export type OpenJobCard = {
  id: string;
  projectId: string;
  title: string;
  workspace: string;
  customer: string;
  lifecycleRef: string | null;
  siteAddress: string | null;
  raisedAt: string;
  installStart: string | null;
  installDays: string[];
  crew: string[];
  isOldestOnProject: boolean;
  completion: JobCardCompletion | null;
  /** Titles of ended cards this card carries the job on from. */
  continuesFrom: string[];
};

export type OpenJobCardSection = {
  id: OpenJobCardSectionId;
  title: string;
  hint: string;
  cards: OpenJobCard[];
};

/**
 * The latest completion event decides; a later release reopens the card.
 * An ended card counts as closed too: its crew are done and the job carries on under a later card.
 */
export function jobCardCompletion(events: JobCompletionRow[]): JobCardCompletion | null {
  const latest = [...events].sort((a, b) => Date.parse(a.attested_at) - Date.parse(b.attested_at)).slice(-1)[0];
  if (!latest) return null;
  const by = String(latest.actor_name || '').trim() || 'Crew';
  if (latest.action === 'ended') return { at: latest.attested_at, by, kind: 'ended' };
  const complete = latest.action ? latest.action === 'completed' : latest.job_complete === true;
  return complete ? { at: latest.attested_at, by, kind: 'completed' } : null;
}

export function isJobCardComplete(events: JobCompletionRow[]): boolean {
  return jobCardCompletion(events) !== null;
}

export function groupCompletionsByCard(completions: JobCompletionRow[]): Map<string, JobCompletionRow[]> {
  const byCard = new Map<string, JobCompletionRow[]>();
  completions.forEach((row) => {
    const id = String(row.job_card_id || '');
    if (!id) return;
    byCard.set(id, [...(byCard.get(id) || []), row]);
  });
  return byCard;
}

export type CarryingCard = Pick<JobCardRow, 'id' | 'install_date_override' | 'install_end_date_override' | 'follows_booking'>;

/** Cards still carrying the job: an ended card hands the job on to a later card, so its dates no longer count. */
export function carryingCards<T extends { id: string }>(cards: T[], completionsByCard: Map<string, JobCompletionRow[]>): T[] {
  return cards.filter((card) => jobCardCompletion(completionsByCard.get(card.id) || [])?.kind !== 'ended');
}

/** Sorted on-site days across the cards still carrying the job. */
export function carryingInstallDays(
  cards: CarryingCard[],
  completionsByCard: Map<string, JobCompletionRow[]>,
  booking: InstallCalendarEvent | null,
): string[] {
  const days = carryingCards(cards, completionsByCard).flatMap((card) => jobCardInstallSpan({
    calendar: booking,
    overrideStart: card.install_date_override,
    overrideEnd: card.install_end_date_override,
    followsBooking: card.follows_booking !== false,
  }).days);
  return Array.from(new Set(days)).sort();
}

export function formatCompletionStatus(completion: JobCardCompletion | null): string {
  if (!completion) return 'Not marked complete';
  const date = new Date(completion.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });
  if (completion.kind === 'ended') return `Ended by ${completion.by} on ${date}, continued on a later card`;
  return `Marked complete by ${completion.by} on ${date}`;
}

export function assembleOpenJobCards(input: {
  cards: JobCardRow[];
  completions: JobCompletionRow[];
  projects: OpenJobCardProject[];
  events: InstallEventRow[];
  assignees: Array<{ job_card_id: string; name: string }>;
}): OpenJobCard[] {
  const completionsByCard = groupCompletionsByCard(input.completions);
  const oldestByProject = new Map<string, JobCardRow>();
  input.cards.forEach((card) => {
    const current = oldestByProject.get(card.project_id);
    if (!current || Date.parse(card.created_at) < Date.parse(current.created_at)) {
      oldestByProject.set(card.project_id, card);
    }
  });
  const crewByCard = new Map<string, string[]>();
  input.assignees.forEach((row) => {
    crewByCard.set(row.job_card_id, [...(crewByCard.get(row.job_card_id) || []), row.name]);
  });
  const projectById = new Map(input.projects.map((project) => [project.id, project]));
  const calendarByProject = latestInstallEventsByProject(input.events);

  const withCompletion = input.cards.map((card) => ({ card, completion: jobCardCompletion(completionsByCard.get(card.id) || []) }));
  // An ended card hands the job on to a later card: list the job once, on the card that carries on.
  const continuesFrom = new Map<string, string[]>();
  withCompletion.forEach(({ card, completion }) => {
    if (completion?.kind !== 'ended') return;
    const events = [...(completionsByCard.get(card.id) || [])]
      .sort((a, b) => Date.parse(a.attested_at) - Date.parse(b.attested_at));
    const next = events[events.length - 1]?.continued_on_card_id;
    if (!next) return;
    continuesFrom.set(next, [...(continuesFrom.get(next) || []), String(card.title || '').trim() || 'Job card']);
  });
  return withCompletion
    .filter(({ completion }) => completion?.kind !== 'ended')
    .filter(({ card, completion }) => {
      if (!completion) return true;
      const project = projectById.get(card.project_id);
      return !!project && workspaceLifecycleStatus(project) === 'Active';
    })
    .map(({ card, completion }) => {
      const project = projectById.get(card.project_id);
      const calendar: InstallCalendarEvent | null = calendarByProject.get(card.project_id) || null;
      const span = jobCardInstallSpan({
        calendar,
        fallbackStart: project?.install_date || null,
        overrideStart: installDateOverrideFrom(
          { id: card.id, install_date_override: card.install_date_override ?? null },
          { metadata: project?.metadata },
        ),
        overrideEnd: card.install_end_date_override ?? null,
        followsBooking: card.follows_booking !== false,
      });
      return {
        id: card.id,
        projectId: card.project_id,
        title: String(card.title || '').trim() || 'Job card',
        workspace: String(project?.project_name || '').trim() || 'Workspace',
        customer: String(project?.customer_name || '').trim(),
        lifecycleRef: project?.lifecycle_ref || null,
        siteAddress: project?.site_address || null,
        raisedAt: card.created_at,
        installStart: span.start,
        installDays: span.days,
        crew: crewByCard.get(card.id) || [],
        isOldestOnProject: oldestByProject.get(card.project_id)?.id === card.id,
        completion,
        continuesFrom: continuesFrom.get(card.id) || [],
      };
    });
}

function mondayOf(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
  return shiftDayKey(dayKey, -((weekday + 6) % 7));
}

export function openJobCardSectionId(
  card: Pick<OpenJobCard, 'installDays'> & { completion?: JobCardCompletion | null },
  todayKey: string,
): OpenJobCardSectionId {
  const days = card.installDays;
  if (!days.length) return 'no_date';
  const first = days[0];
  const last = days[days.length - 1];
  if (last < todayKey) return card.completion ? 'installed' : 'overdue';
  const nextMonday = shiftDayKey(mondayOf(todayKey), 7);
  const followingMonday = shiftDayKey(nextMonday, 7);
  if (first < nextMonday) return 'this_week';
  if (first < followingMonday) return 'next_week';
  return 'later';
}

export function sectionOpenJobCards(cards: OpenJobCard[], now = new Date()): OpenJobCardSection[] {
  const todayKey = londonDayKey(now.toISOString());
  // Chronological by first on-site day, then booked time. A card with no time covers the whole day, so
  // it leads that day. When raised only breaks exact ties.
  const startTime = (card: OpenJobCard) => toInstallTimeOnly(card.installStart) || '';
  const byInstall = (a: OpenJobCard, b: OpenJobCard) => (
    (a.installDays[0] || '').localeCompare(b.installDays[0] || '')
    || startTime(a).localeCompare(startTime(b))
    || Date.parse(a.raisedAt) - Date.parse(b.raisedAt)
    || a.workspace.localeCompare(b.workspace)
  );
  const byRaised = (a: OpenJobCard, b: OpenJobCard) => (
    Date.parse(a.raisedAt) - Date.parse(b.raisedAt) || a.workspace.localeCompare(b.workspace)
  );
  return OPEN_JOB_CARD_SECTIONS.map((section) => ({
    ...section,
    cards: cards
      .filter((card) => openJobCardSectionId(card, todayKey) === section.id)
      .sort(section.id === 'no_date' ? byRaised : byInstall),
  }));
}
